import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { getIdToken } from '../attendedUtils';

function EditProject() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [dupWarning, setDupWarning] = useState(null);

    const [form, setForm] = useState({
        date_start: '',
        date_end: '',
        estimated_value: '',
    });

    useEffect(() => {
        axios.get(`${config.API_URL}/projects/${id}`)
            .then(res => {
                const p = res.data;
                setProject(p);
                // Convert MM/DD/YYYY back to YYYY-MM-DD for date inputs
                const toInput = (mmddyyyy) => {
                    if (!mmddyyyy) return '';
                    const [m, d, y] = mmddyyyy.split('/');
                    return `${y}-${m}-${d}`;
                };
                setForm({
                    date_start: toInput(p.date_start),
                    date_end: toInput(p.date_end),
                    estimated_value: p.estimated_value,
                });
                setLoading(false);
            })
            .catch(() => {
                setError('Failed to load project.');
                setLoading(false);
            });
    }, [id]);

    const today = new Date().toISOString().split('T')[0];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setError(null);
        setDupWarning(null);
    };

    const handleSubmit = async () => {
        if (form.date_end > today) { setError('End date cannot be in the future.'); return; }
        if (form.date_start > form.date_end) { setError('Start date must be before end date.'); return; }
        if (!form.estimated_value || parseInt(form.estimated_value) < 1) { setError('Estimated value must be at least 1.'); return; }

        setSaving(true);
        setError(null);
        setDupWarning(null);
        try {
            const token = await getIdToken();
            await axios.put(`${config.API_URL}/projects/${id}`, {
                date_start: form.date_start,
                date_end: form.date_end,
                estimated_value: parseInt(form.estimated_value),
            }, { headers: { Authorization: token } });
            navigate(`/projects/${id}`);
        } catch (err) {
            if (err.response?.status === 409) {
                setDupWarning(err.response.data.existing);
            } else {
                setError(err.response?.data?.error || 'Failed to update project.');
            }
            setSaving(false);
        }
    };

    if (loading) return <div style={styles.message}>Loading...</div>;
    if (error && !project) return <div style={styles.error}>{error}</div>;

    return (
        <div style={styles.container}>
            <Link to={`/projects/${id}`} style={styles.backLink}>← Back to Project</Link>
            <h1 style={styles.title}>Edit Project</h1>
            {project && <div style={styles.projectName}>{project.name}</div>}

            {error && <div style={styles.errorBox}>{error}</div>}
            {dupWarning && (
                <div style={styles.dupBox}>
                    <strong>⚠ A project with this scope already exists.</strong>
                    <div style={{ marginTop: '10px' }}>
                        <Link to={`/projects/${dupWarning.id}`} style={styles.dupLink}>→ View existing project</Link>
                    </div>
                </div>
            )}

            <div style={styles.form}>
                <div style={styles.sectionHeader}>Date Range</div>

                <div style={styles.twoCol}>
                    <div style={styles.field}>
                        <label style={styles.label}>Start Date *</label>
                        <input type="date" name="date_start" value={form.date_start} onChange={handleChange} max={today} style={styles.input} />
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>End Date *</label>
                        <input type="date" name="date_end" value={form.date_end} onChange={handleChange} max={today} style={styles.input} />
                    </div>
                </div>

                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Target</div>

                <div style={styles.field}>
                    <label style={styles.label}>Estimated Value (number of shows)</label>
                    <input
                        type="number"
                        name="estimated_value"
                        value={form.estimated_value}
                        onChange={handleChange}
                        min="1"
                        style={styles.input}
                    />
                </div>

                <div style={{ ...styles.sectionDivider, marginBottom: '24px' }} />
                <button onClick={handleSubmit} disabled={saving} style={styles.submitButton}>
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: { maxWidth: '680px', margin: '0 auto' },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '24px' },
    title: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '8px' },
    projectName: { fontSize: '14px', color: '#666', marginBottom: '32px' },
    form: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '32px' },
    sectionHeader: { fontSize: '16px', fontWeight: 'bold', color: '#e0e0e0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px' },
    sectionDivider: { borderTop: '1px solid #2a2a2a', margin: '28px 0 24px' },
    field: { marginBottom: '20px' },
    label: { display: 'block', fontSize: '13px', color: '#aaa', marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '10px 14px', fontSize: '15px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    submitButton: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '12px 24px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', width: '100%', marginTop: '8px' },
    errorBox: { backgroundColor: '#2c1a1e', border: '1px solid #cf6679', borderRadius: '4px', padding: '12px 16px', color: '#cf6679', marginBottom: '24px', fontSize: '14px' },
    dupBox: { backgroundColor: '#1e1a0e', border: '1px solid #f0a500', borderRadius: '4px', padding: '14px 16px', color: '#f0c060', marginBottom: '24px', fontSize: '14px', lineHeight: '1.5' },
    dupLink: { color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },
};

export default EditProject;
